/**
 * @fileoverview Riddle - Contenedor Principal de la Arena de Adivina Adivinador en Pantalla Gigante (/screen).
 * Procesa en tiempo real los 4 Layouts Modulares:
 * 1. Clásico (Imagen Izquierda 45%, Opciones Derecha 55%)
 * 2. Invertido (Opciones Izquierda 55%, Imagen Derecha 45%)
 * 3. Solo Imagen (Centrada y ensanchada hasta 85vw / 1400px máx con object-fit: contain)
 * 4. Solo Opciones (Cuadrícula 2x2 en CSS Grid)
 * 
 * Implementa GPU Layer Compositing a 60 FPS, transición de revelado y resaltado de respuesta correcta.
 * 
 * @module components/Screen/Sections/Riddle
 */

import React, { useMemo } from 'react';
import { RiddleLayoutClassic } from './Riddle/RiddleLayoutClassic';
import { RiddleLayoutInverted } from './Riddle/RiddleLayoutInverted';
import { RiddleLayoutOnlyImage } from './Riddle/RiddleLayoutOnlyImage';
import { RiddleLayoutOnlyOptions } from './Riddle/RiddleLayoutOnlyOptions';

/**
 * Resuelve la URL absoluta de un recurso multimedia.
 * @param {string} url - URL relativa o absoluta.
 * @returns {string} URL resuelta.
 */
const resolveMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
        return url;
    }
    const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
    return `${apiBase}${url.startsWith('/') ? url.substring(1) : url}`;
};

/**
 * Componente Contenedor Principal de la Pantalla Gigante para Adivina Adivinador.
 * 
 * @param {Object} props
 * @param {Object} props.riddle - Objeto con datos de la pregunta/adivinanza activa.
 * @param {boolean} [props.isActiveWomensMode] - Modo Femenil o Varonil para fondos por defecto.
 * @returns {React.ReactElement}
 */
export const Riddle = ({ riddle, isActiveWomensMode }) => {
    // Si no hay datos, renderizamos un contenedor vacío de seguridad
    const activeRiddle = riddle || {};

    // Layout seleccionado (1: Clásico, 2: Invertido, 3: Solo Imagen, 4: Solo Opciones)
    const layoutMode = Number(activeRiddle.screenLayout || 1);
    const isItemVisible = !!activeRiddle.isItemVisible;
    const showCorrectAnswer = activeRiddle.showCorrectAnswer !== false; // Default true

    // Textos y Estilos
    const instruction = activeRiddle.instruction || activeRiddle.headerTitle || 'TRIVIA DOMINGUERA';
    const brandLogoUrl = resolveMediaUrl(activeRiddle.brandLogoUrl) || '/img/page/adsl-five.png';
    const sponsorLogoUrl = resolveMediaUrl(activeRiddle.sponsorLogoUrl);
    const fontFamily = activeRiddle.fontFamily ? `"${activeRiddle.fontFamily}", "GFS Didot", serif` : '"GFS Didot", serif';
    const textColor = activeRiddle.textColor || activeRiddle.gameTextColor || '#FFFFFF';

    // Manejo de fondo dinámico
    const backgroundStyle = useMemo(() => {
        if (activeRiddle.backgroundType === 'image' && activeRiddle.backgroundImageUrl) {
            return {
                backgroundImage: `url(${resolveMediaUrl(activeRiddle.backgroundImageUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat'
            };
        }
        if (activeRiddle.backgroundType === 'color' && (activeRiddle.backgroundColor || activeRiddle.gameBackgroundColor)) {
            return {
                backgroundColor: activeRiddle.backgroundColor || activeRiddle.gameBackgroundColor
            };
        }
        return {};
    }, [activeRiddle.backgroundType, activeRiddle.backgroundImageUrl, activeRiddle.backgroundColor, activeRiddle.gameBackgroundColor]);

    const hasCustomBg = !!(activeRiddle.backgroundImageUrl || activeRiddle.backgroundColor || activeRiddle.gameBackgroundColor);

    return (
        <div className="RiddleGame__Screen" style={backgroundStyle}>
            {/* Fondo institucional por defecto */}
            {!hasCustomBg && (
                <div className="RiddleGame__Screen__DefaultBackground">
                    {isActiveWomensMode ? (
                        <img
                            src="/img/page/backgrounds/barrio9/background-1.png"
                            alt="Fondo Femenil"
                            className="RiddleGame__Screen__DefaultBackground__Image"
                        />
                    ) : (
                        <img
                            src="/img/page/backgrounds/background.png"
                            alt="Fondo Varonil"
                            className="RiddleGame__Screen__DefaultBackground__Image"
                        />
                    )}
                </div>
            )}

            {/* Capa de iluminación ambiental GPU */}
            <div className="RiddleGame__Screen__AmbientGlow" />

            {/* Encabezado Institucional Fijo Superior */}
            <header className="RiddleGame__Screen__Header">
                <div className="RiddleGame__Screen__Header__Branding">
                    <img
                        src={brandLogoUrl}
                        alt="ADSL"
                        className="RiddleGame__Screen__Header__BrandLogo"
                    />

                    {sponsorLogoUrl && (
                        <div className="RiddleGame__Screen__Header__Sponsor">
                            <span className="RiddleGame__Screen__Header__SponsorLabel" style={{ fontFamily }}>
                                PRESENTA
                            </span>
                            <img
                                src={sponsorLogoUrl}
                                alt="Patrocinador"
                                className="RiddleGame__Screen__Header__SponsorLogo"
                            />
                        </div>
                    )}
                </div>

                <h1
                    className="RiddleGame__Screen__Header__Instruction"
                    style={{ fontFamily, color: textColor }}
                >
                    {/* {instruction} */}
                    Trivia dominguera
                </h1>
            </header>

            {/* Arena Central: Matriz de los 4 Layouts Modulares */}
            <main className="RiddleGame__Screen__Arena">
                {layoutMode === 1 && (
                    <RiddleLayoutClassic
                        riddle={activeRiddle}
                        isItemVisible={isItemVisible}
                        showCorrectAnswer={showCorrectAnswer}
                        fontFamily={fontFamily}
                        textColor={textColor}
                    />
                )}

                {layoutMode === 2 && (
                    <RiddleLayoutInverted
                        riddle={activeRiddle}
                        isItemVisible={isItemVisible}
                        showCorrectAnswer={showCorrectAnswer}
                        fontFamily={fontFamily}
                        textColor={textColor}
                    />
                )}

                {layoutMode === 3 && (
                    <RiddleLayoutOnlyImage
                        riddle={activeRiddle}
                        isItemVisible={isItemVisible}
                        fontFamily={fontFamily}
                        textColor={textColor}
                    />
                )}

                {layoutMode === 4 && (
                    <RiddleLayoutOnlyOptions
                        riddle={activeRiddle}
                        isItemVisible={isItemVisible}
                        showCorrectAnswer={showCorrectAnswer}
                        fontFamily={fontFamily}
                        textColor={textColor}
                    />
                )}
            </main>

            {/* Pie de Pantalla Fijo */}
            <footer className="RiddleGame__Screen__Footer FooterCredits">
                Hecho con ♥ por Interaktia | 2026
            </footer>
        </div>
    );
};

export default Riddle;